/**
 * Decide what a single contextual quest button should do at an NPC.
 *
 * Quest dialogue is not controller-navigable, so a pad cannot currently drive
 * the accept → complete loop. This collapses that loop into one decision:
 * given the quests an NPC is offering and the player's level, either hand one
 * in or take one on.
 *
 * Turn-in wins over accept. Handing in a finished quest is what unblocks the
 * next step of a chain, and a player standing at an NPC with both a reward
 * waiting and a new offer expects the reward first.
 *
 * The predicates mirror the fallback branches in QuestOfferWindow so the
 * button and the on-screen list can never disagree about what is actionable.
 */

export interface ContextualQuest {
  id: string;
  requiredLevel?: number;
  isAccepted?: boolean;
  isCompleted?: boolean;
  isTurnInReady?: boolean;
}

export type ContextualQuestAction =
  | { action: 'completeQuest'; questId: string }
  | { action: 'acceptQuest'; questId: string };

/** Accepted, objectives met, not yet handed in. */
export function isTurnInReady(quest: ContextualQuest): boolean {
  return Boolean(quest.isAccepted && quest.isTurnInReady && !quest.isCompleted);
}

/** Not yet taken, not already finished, and the player meets the level gate. */
export function isAcceptable(quest: ContextualQuest, playerLevel: number): boolean {
  if (quest.isAccepted || quest.isCompleted) return false;
  return playerLevel >= (quest.requiredLevel ?? 1);
}

export function resolveContextualQuestAction(
  quests: readonly ContextualQuest[] | null | undefined,
  playerLevel: number,
): ContextualQuestAction | null {
  if (!quests?.length) return null;

  // Turn-in first — see the precedence note above.
  const turnIn = quests.find(isTurnInReady);
  if (turnIn) return { action: 'completeQuest', questId: turnIn.id };

  const accept = quests.find(q => isAcceptable(q, playerLevel));
  if (accept) return { action: 'acceptQuest', questId: accept.id };

  return null;
}
